import React, {Component} from 'react';
import {withTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {API_URL} from "../../settings/local_settings";
import {APIClient} from "../../utils/APIClient";
import {FaIcon} from "../FaIcon";
import {Spinner} from "../Spinner";
import CustomMetricEditor from "./CustomMetricEditor/CustomMetricEditor";
import {YesNoModal} from "../elements/YesNoModal";
import {AgencyHeader} from "./AgencyHeader";
import {AgencyTabs} from "./AgencyTabs";
import {PersonalizationSubTabs} from "./PersonalizationSubTabs";
import "./CustomMetrics.scss";


class CustomMetricsPageContent extends Component {

    constructor(props) {
        super(props);
        this.state = {
            customMetrics: [],
            loaded: false,
            editorOpen: false,
            editorMetric: null,        // null = crear; objeto = editar
            deleteCandidate: null,
        };
        this.handleCreateClick = this.handleCreateClick.bind(this);
        this.handleEditClick = this.handleEditClick.bind(this);
        this.handleCloseEditor = this.handleCloseEditor.bind(this);
        this.handleSaved = this.handleSaved.bind(this);
        this.handleDeleteClick = this.handleDeleteClick.bind(this);
        this.handleDeleteConfirm = this.handleDeleteConfirm.bind(this);
        this.handleDeleteCancel = this.handleDeleteCancel.bind(this);
    }

    componentDidMount() {
        this.fetchCustomMetrics();
    }

    fetchCustomMetrics() {
        new APIClient(API_URL + 'agency/custom-metrics/').call('GET').then((response) => {
            const items = (response && response.custom_metrics) || [];
            this.setState({customMetrics: items, loaded: true});
        }).catch(() => {
            this.setState({loaded: true});
        });
    }

    handleCreateClick() {
        this.setState({editorOpen: true, editorMetric: null});
    }

    handleEditClick(metric) {
        this.setState({editorOpen: true, editorMetric: metric});
    }

    handleCloseEditor() {
        this.setState({editorOpen: false, editorMetric: null});
    }

    handleSaved(savedMetric, action) {
        const {t} = this.props;
        toast.success(action === 'create'
            ? t('custom_metrics.toast.created', 'Métrica creada')
            : t('custom_metrics.toast.updated', 'Métrica actualizada'));
        this.handleCloseEditor();
        this.fetchCustomMetrics();
    }

    handleDeleteClick(metric) {
        this.setState({deleteCandidate: metric});
    }

    handleDeleteCancel() {
        this.setState({deleteCandidate: null});
    }

    handleDeleteConfirm() {
        const {t} = this.props;
        const metric = this.state.deleteCandidate;
        if (!metric) return;

        new APIClient(API_URL + 'agency/custom-metrics/' + metric.id + '/').call('DELETE')
            .then((response) => {
                if (!response) {
                    toast.error(t('custom_metrics.toast.delete_error', 'Error al eliminar la métrica'));
                    return;
                }
                if (response.error_code === 'IN_USE') {
                    const blockers = (response.error_details && response.error_details.blockers) || [];
                    const detail = blockers.map(b => `${b.kind}: ${b.count}`).join(', ');
                    toast.error(t('custom_metrics.toast.in_use', 'No se puede eliminar — en uso: ') + detail);
                    return;
                }
                if (response.error_code || response.status === 'ERROR') {
                    toast.error(response.error_message || t('custom_metrics.toast.delete_error', 'Error al eliminar la métrica'));
                    return;
                }
                toast.success(t('custom_metrics.toast.deleted', 'Métrica eliminada'));
                this.fetchCustomMetrics();
            })
            .catch(() => {
                toast.error(t('custom_metrics.toast.delete_error', 'Error al eliminar la métrica'));
            })
            .finally(() => {
                this.setState({deleteCandidate: null});
            });
    }

    renderEmptyState(t) {
        return (
            <div className="box custom-metric-empty">
                <div className="custom-metric-empty-icon">
                    <FaIcon iconName="calculator"/>
                </div>
                <h3 className="custom-metric-empty-title">
                    {t('custom_metrics.empty.title', 'Todavía no creaste métricas personalizadas')}
                </h3>
                <p className="custom-metric-empty-subtitle">
                    {t('custom_metrics.empty.subtitle', 'Arma fórmulas con métricas existentes y constantes, por ejemplo «ventas / impresiones» o «conversiones × 0,5».')}
                </p>
                <button className="btn -alt custom-metric-empty-cta" onClick={this.handleCreateClick}>
                    <FaIcon iconName="plus"/> {t('custom_metrics.create_first', 'Crear primera métrica')}
                </button>
            </div>
        );
    }

    renderUsageText(count) {
        const {t} = this.props;
        if (count === 0) return t('custom_metrics.card.no_usage', 'Sin uso aún');
        if (count === 1) return t('custom_metrics.card.usage_one', 'Objetivo de 1 línea');
        return t('custom_metrics.card.usage_many', 'Objetivo de {{count}} líneas', {count});
    }

    renderMetricCard(metric, t) {
        return (
            <div key={metric.id} className="box custom-metric-card">
                <div className="custom-metric-card-row">
                    <div className="custom-metric-card-main">
                        <h3 className="custom-metric-card-title">
                            {metric.display_name}
                            {metric.has_broken_reference && (
                                <span className="custom-metric-card-broken">
                                    <FaIcon iconName="exclamation-triangle"/>{' '}
                                    {t('custom_metrics.broken_reference_badge', 'Referencia rota')}
                                </span>
                            )}
                        </h3>
                        <p className="custom-metric-card-formula">{metric.formula}</p>
                        <div className="custom-metric-card-meta">
                            <span>
                                {t('custom_metrics.card.format', 'Formato')}: <strong>{metric.value_format}</strong>
                            </span>
                            <span>
                                {t('custom_metrics.card.direction', 'Más alto es mejor')}: <strong>{metric.higher_is_better ? t('common.yes', 'Sí') : t('common.no', 'No')}</strong>
                            </span>
                            <span className={`cm-card__usage ${metric.goals_count === 0 ? '-empty' : ''}`}>
                                {this.renderUsageText(metric.goals_count)}
                            </span>
                        </div>
                    </div>
                    <div className="custom-metric-card-actions">
                        <button className="btn -negative" onClick={() => this.handleEditClick(metric)}>
                            <FaIcon iconName="edit"/> {t('custom_metrics.card.edit', 'Editar')}
                        </button>
                        <button className="btn -alt -negative" onClick={() => this.handleDeleteClick(metric)}>
                            <FaIcon iconName="trash"/> {t('custom_metrics.card.delete', 'Eliminar')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const {t} = this.props;
        const {customMetrics, loaded, editorOpen, editorMetric, deleteCandidate} = this.state;

        return (
            <section className="section-content">
                <AgencyHeader/>
                <AgencyTabs activeTab="-seventh"/>
                <PersonalizationSubTabs activeSubTab="metrics"/>

                <div className="section-body custom-metric-page-body">
                    <div className="custom-metric-page-header-row">
                        <p className="custom-metric-page-intro">
                            <FaIcon iconName="info-circle"/>{' '}
                            {t('custom_metrics.page.visibility_copy',
                                'Las métricas que creés acá las ve y puede editar cualquier usuario de tu agencia. Todos los cambios quedan registrados en la bitácora.')}
                        </p>
                        {loaded && customMetrics.length > 0 && (
                            <button className="btn -alt" onClick={this.handleCreateClick}>
                                <FaIcon iconName="plus"/> {t('custom_metrics.create_new', 'Nueva métrica')}
                            </button>
                        )}
                    </div>

                    {!loaded && (
                        <div className="box-loader">
                            <Spinner color="positive"/>
                        </div>
                    )}

                    {loaded && customMetrics.length === 0 && this.renderEmptyState(t)}

                    {loaded && customMetrics.length > 0 && (
                        <div className="custom-metric-list">
                            {customMetrics.map(metric => this.renderMetricCard(metric, t))}
                        </div>
                    )}
                </div>

                {editorOpen && (
                    <CustomMetricEditor
                        isOpen={editorOpen}
                        metric={editorMetric}
                        onClose={this.handleCloseEditor}
                        onSaved={this.handleSaved}
                    />
                )}

                {deleteCandidate && (
                    <YesNoModal
                        isOpen={true}
                        skipRenderButton={true}
                        title={t('custom_metrics.delete.title', 'Eliminar métrica')}
                        question={(deleteCandidate.goals_count > 0
                            ? t('custom_metrics.delete.warning_with_goals',
                                '⚠ Esta métrica está siendo usada en {{count}} goals activos. Al eliminarla, los goals quedarán huérfanos. ¿Continuar?',
                                {count: deleteCandidate.goals_count})
                            : t('custom_metrics.delete.confirm',
                                '¿Eliminar "{{name}}"? Esta acción no se puede deshacer.',
                                {name: deleteCandidate.display_name}))}
                        onYesAnswer={this.handleDeleteConfirm}
                        onNoAnswer={this.handleDeleteCancel}
                    />
                )}
            </section>
        );
    }
}

const CustomMetricsPageContentWithT = withTranslation()(CustomMetricsPageContent);
export {CustomMetricsPageContentWithT as CustomMetricsPageContent};
